import React from "react";
import { ArrowUpRight, ArrowDownRight, Info, Star } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** Horizontal share bar; `value` is 0–1. */
export function RatioBar({
  value,
  className,
  barClassName,
}: {
  value: number;
  className?: string;
  barClassName?: string;
}) {
  const pct = Math.max(0, Math.min(100, (Number.isFinite(value) ? value : 0) * 100));

  return (
    <div
      className={cn(
        "h-2.5 min-w-[3.25rem] flex-1 overflow-hidden rounded-sm border border-border/70 bg-muted/50",
        className
      )}
      role="meter"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn(
          "h-full rounded-[1px] bg-amber-400 transition-[width] duration-300 ease-out dark:bg-amber-500",
          barClassName
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span
            className="inline-flex shrink-0 cursor-help items-center justify-center leading-none text-muted-foreground/70"
            aria-label="More info"
          >
            <Info className="h-3.5 w-3.5" />
          </span>
        }
      />
      <Tooltip.Content className="font-sans">{text}</Tooltip.Content>
    </Tooltip>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Rising volume bars for review count heroes. Prefer real `points` when available. */
export function HeroVolumeGraphic({
  count,
  points,
  currentIndex,
}: {
  count: number;
  points?: { value: number; label: string }[];
  currentIndex?: number;
}) {
  const series = points && points.length > 0 ? points : null;
  const heights = series
    ? (() => {
        const max = Math.max(...series.map(p => p.value), 1);
        return series.map(p => (p.value <= 0 ? 0.12 : 0.18 + (p.value / max) * 0.82));
      })()
    : (() => {
        const intensity = clamp(Math.log10(Math.max(count, 1) + 1) / 3, 0.2, 1);
        return [0.35, 0.55, 0.42, 0.78, 0.62, 0.9, 0.7].map(
          (h, i) => h * (0.55 + intensity * 0.45) * (0.85 + (i % 3) * 0.05)
        );
      })();
  const activeIndex = series != null ? currentIndex ?? heights.length - 1 : -1;

  return (
    <div className="flex min-w-24 items-end justify-center gap-1.5">
      {heights.map((h, i) => {
        const isCurrent = i === activeIndex;
        const point = series?.[i];
        const bar = (
          <span
            className={cn(
              "block rounded-full bg-gradient-to-t shadow-sm transition-[height,width,box-shadow] duration-500 ease-out",
              isCurrent
                ? "w-2.5 from-sky-700 to-sky-400 shadow-[0_0_0_2px_rgba(14,165,233,0.35)] dark:from-sky-400 dark:to-sky-200 dark:shadow-[0_0_0_2px_rgba(56,189,248,0.35)]"
                : "w-2 from-sky-500/55 to-sky-300/55 dark:from-sky-600/50 dark:to-sky-400/50"
            )}
            style={{ height: `${Math.round(h * 64)}px` }}
          />
        );

        if (!point) {
          return (
            <span key={i} aria-hidden>
              {bar}
            </span>
          );
        }

        return (
          <Tooltip key={`${point.label}-${i}`}>
            <Tooltip.Trigger
              render={
                <button
                  type="button"
                  className="inline-flex cursor-default items-end border-0 bg-transparent p-0"
                  aria-label={`${point.label}: ${point.value.toLocaleString()} reviews`}
                  onClick={event => event.stopPropagation()}
                >
                  {bar}
                </button>
              }
            />
            <Tooltip.Content className="font-sans">
              <span className="font-medium">{point.label}</span>
              <span className="text-background/70"> · {point.value.toLocaleString()}</span>
            </Tooltip.Content>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** Partial star fill for average star rating (0–5). */
export function HeroStarGraphic({ rating }: { rating: number }) {
  const filled = clamp(rating, 0, 5);

  return (
    <div className="flex items-center justify-center gap-1" aria-hidden>
      {Array.from({ length: 5 }, (_, i) => {
        const fill = clamp(filled - i, 0, 1);
        return (
          <span key={i} className="relative inline-flex h-7 w-7">
            <Star
              className="absolute inset-0 h-7 w-7 fill-amber-100 text-amber-200 dark:fill-amber-950 dark:text-amber-800"
              strokeWidth={1.5}
            />
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <Star
                className="h-7 w-7 fill-amber-400 text-amber-500 dark:fill-amber-400 dark:text-amber-300"
                strokeWidth={1.5}
              />
            </span>
          </span>
        );
      })}
    </div>
  );
}

/** Speedometer-style gauge for NPS net score (−100…+100). */
export function HeroNpsGauge({ score }: { score: number }) {
  const normalized = clamp((score + 100) / 200, 0, 1);
  const cx = 80;
  const cy = 78;
  const r = 58;
  // Needle defaults pointing up; rotate from -90° (−100) to +90° (+100)
  const needleRotation = -90 + normalized * 180;

  const polar = (angleDeg: number, radius: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy - radius * Math.sin(rad) };
  };

  const arc = (fromDeg: number, toDeg: number, radius: number) => {
    const start = polar(fromDeg, radius);
    const end = polar(toDeg, radius);
    const large = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${large} 1 ${end.x} ${end.y}`;
  };

  return (
    <svg viewBox="0 0 160 96" className="mx-auto h-[5.5rem] w-[9.5rem]" aria-hidden>
      {/* Zone arcs: detractor / passive / promoter */}
      <path d={arc(180, 120, r)} fill="none" strokeWidth="12" strokeLinecap="butt" className="stroke-rose-400/90" />
      <path d={arc(120, 60, r)} fill="none" strokeWidth="12" strokeLinecap="butt" className="stroke-amber-400/90" />
      <path d={arc(60, 0, r)} fill="none" strokeWidth="12" strokeLinecap="butt" className="stroke-emerald-500/90" />

      {/* Tick marks */}
      {[-100, -50, 0, 50, 100].map(tick => {
        const angle = 180 - ((tick + 100) / 200) * 180;
        const outer = polar(angle, r + 2);
        const inner = polar(angle, r - 10);
        return (
          <line
            key={tick}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            className="stroke-foreground/25"
            strokeWidth="1.5"
          />
        );
      })}

      {/* Needle (points up at 0 rotation) */}
      <g
        style={{
          transform: `rotate(${needleRotation}deg)`,
          transformOrigin: `${cx}px ${cy}px`,
          transition: "transform 500ms ease-out",
        }}
      >
        <polygon
          points={`${cx},${cy - (r - 12)} ${cx - 4},${cy + 2} ${cx + 4},${cy + 2}`}
          className="fill-foreground/80"
        />
      </g>
      <circle cx={cx} cy={cy} r="5.5" className="fill-foreground/80" />
      <circle cx={cx} cy={cy} r="2.5" className="fill-background" />

      {/* Scale labels */}
      <text x="18" y="92" fill="currentColor" className="text-[9px] text-muted-foreground" textAnchor="middle">
        -100
      </text>
      <text x="80" y="92" fill="currentColor" className="text-[9px] text-muted-foreground" textAnchor="middle">
        0
      </text>
      <text x="142" y="92" fill="currentColor" className="text-[9px] text-muted-foreground" textAnchor="middle">
        +100
      </text>
    </svg>
  );
}

/** 1–10 scale with a bar that fills to the average. */
export function HeroTenPointScale({ average }: { average: number }) {
  const value = clamp(average, 0, 10);
  // Bar spans from left edge of "1" through right edge of "10" (10 equal slots)
  const fillPct = (value / 10) * 100;

  return (
    <div className="w-full max-w-[16rem]" aria-hidden>
      <div className="relative mb-1.5 h-2.5 overflow-hidden rounded-full bg-indigo-100 dark:bg-indigo-950">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-400 to-indigo-600 transition-[width] duration-500 ease-out"
          style={{ width: `${fillPct}%` }}
        />
        {value > 0 && (
          <span
            className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border-2 border-background bg-indigo-600 shadow-sm transition-[left] duration-500 ease-out"
            style={{ left: `calc(${fillPct}% - 0.4375rem)` }}
          />
        )}
      </div>
      <div className="flex justify-between px-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
        {Array.from({ length: 10 }, (_, i) => {
          const n = i + 1;
          const reached = value >= n - 0.05;
          return (
            <span
              key={n}
              className={cn(
                "w-4 text-center transition-colors duration-300",
                reached ? "text-indigo-600 dark:text-indigo-400" : "text-muted-foreground/50"
              )}
            >
              {n}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** 100% stacked bar of positive / neutral / negative sentiment shares. */
export function HeroSentimentChart({
  positive,
  neutral,
  negative,
}: {
  positive: number;
  neutral: number;
  negative: number;
}) {
  const total = positive + neutral + negative;
  const segments = [
    {
      key: "positive",
      label: "Positive",
      pct: total > 0 ? (positive / total) * 100 : 0,
      className: "bg-emerald-500",
    },
    {
      key: "neutral",
      label: "Neutral",
      pct: total > 0 ? (neutral / total) * 100 : 0,
      className: "bg-slate-400 dark:bg-slate-500",
    },
    {
      key: "negative",
      label: "Negative",
      pct: total > 0 ? (negative / total) * 100 : 0,
      className: "bg-rose-500",
    },
  ] as const;

  return (
    <div className="w-full max-w-[16rem]" aria-hidden>
      <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-muted">
        {total === 0 ? (
          <div className="h-full w-full bg-muted" />
        ) : (
          segments.map(segment =>
            segment.pct > 0 ? (
              <div
                key={segment.key}
                className={cn("h-full transition-[width] duration-500 ease-out", segment.className)}
                style={{ width: `${segment.pct}%` }}
                title={`${segment.label}: ${Math.round(segment.pct)}%`}
              />
            ) : null
          )
        )}
      </div>
      <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
        {segments.map(segment => (
          <span
            key={segment.key}
            className="inline-flex items-center gap-1 text-[11px] font-medium tabular-nums text-muted-foreground"
          >
            <span className={cn("h-2 w-2 rounded-full", segment.className)} />
            {Math.round(segment.pct)}%
          </span>
        ))}
      </div>
    </div>
  );
}

/** Dual-arc ring: replied (teal) + skipped (slate) of total handled share. */
export function HeroReplyGraphic({
  replied,
  skipped,
  needingReply,
}: {
  replied: number;
  skipped: number;
  needingReply: number;
}) {
  const total = Math.max(replied + skipped + needingReply, 0);
  const r = 26;
  const c = 2 * Math.PI * r;
  const repliedLen = total > 0 ? (replied / total) * c : 0;
  const skippedLen = total > 0 ? (skipped / total) * c : 0;

  return (
    <svg viewBox="0 0 64 64" className="mx-auto h-16 w-16 -rotate-90" aria-hidden>
      <circle cx="32" cy="32" r={r} fill="none" strokeWidth="7" className="stroke-teal-100 dark:stroke-teal-950" />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        strokeWidth="7"
        strokeLinecap="butt"
        className="stroke-teal-500 transition-[stroke-dasharray] duration-500 ease-out"
        strokeDasharray={`${repliedLen} ${c}`}
      />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        strokeWidth="7"
        strokeLinecap="butt"
        className="stroke-slate-400 transition-[stroke-dasharray,stroke-dashoffset] duration-500 ease-out dark:stroke-slate-500"
        strokeDasharray={`${skippedLen} ${c}`}
        strokeDashoffset={-repliedLen}
      />
    </svg>
  );
}

export function MetricHero({
  value,
  label,
  delta,
  formatDelta = (v: number) => String(v),
  allTimeValue,
  graphic,
  onClick,
  className,
}: {
  value: string;
  label: string;
  delta?: number | null;
  formatDelta?: (v: number) => string;
  allTimeValue?: string | null;
  graphic: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const clickable = !!onClick;
  const isFlat = delta != null && Math.abs(delta) < 0.005;
  const isUp = delta != null && delta > 0;
  const hasMeta = delta != null || allTimeValue != null;

  return (
    <div
      className={cn(
        "flex flex-col items-center border-b border-border/50 px-4 py-5 text-center",
        clickable && "cursor-pointer transition-colors hover:bg-muted/35",
        className
      )}
      onClick={onClick}
      onKeyDown={
        clickable
          ? event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? "link" : undefined}
      aria-label={clickable ? `View ${label} reviews` : undefined}
    >
      <div className="flex h-24 w-full shrink-0 items-center justify-center">{graphic}</div>
      <div className="mt-2 flex min-h-[4.75rem] w-full min-w-0 flex-col items-center">
        <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2">
          <div aria-hidden className="min-w-0" />
          <p className="indent-px max-w-full text-center text-4xl font-semibold tabular-nums text-foreground">
            {value}
          </p>
          <div className="min-w-0 justify-self-start">
            {hasMeta && (
              <div className="flex flex-col items-start gap-0.5 text-left text-[11px] leading-tight text-muted-foreground/80">
                {delta != null && (
                  <span
                    className={cn(
                      "inline-flex max-w-full flex-wrap items-center gap-0.5 tabular-nums",
                      !isFlat && isUp && "text-emerald-600/80 dark:text-emerald-400/80",
                      !isFlat && !isUp && "text-rose-600/80 dark:text-rose-400/80"
                    )}
                  >
                    {!isFlat &&
                      (isUp ? (
                        <ArrowUpRight className="h-3 w-3 shrink-0" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3 shrink-0" />
                      ))}
                    {isFlat ? "—" : `${isUp ? "+" : ""}${formatDelta(delta)}`}
                  </span>
                )}
                {allTimeValue != null && (
                  <span className="max-w-full truncate tabular-nums">
                    {allTimeValue}
                    <span className="text-muted-foreground/60"> all-time</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}
